import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";
import {
  fetchNumberOfRentalPropertiesConstructed,
  RentalPropertiesConstructedPoint,
} from "src/site/estat";
import { ChartSpec, debounce, isSp, renderChart } from "src/site/recharts";

// Site::PrefectureMansion::ConstructionCount::Component の Stimulus コントローラ。
// landIeul: hooks/eStat/useNumberOfNewRentalHouses + domainParts/NumberOfRentalPropertiesGraph。
// グラフの spec は apartment の NumberOfRentalPropertiesConstructed と同一 (外枠と説明文だけが違う)。

class SitePrefectureMansionConstructionCountController extends Controller<HTMLElement> {
  static targets = ["graph", "descriptionIncrease", "descriptionDecrease"];
  static values = { areaCode: String };

  declare readonly graphTarget: HTMLElement;
  declare readonly descriptionIncreaseTarget: HTMLElement;
  declare readonly descriptionDecreaseTarget: HTMLElement;
  declare readonly areaCodeValue: string;

  private graphData: RentalPropertiesConstructedPoint[] = [];
  private onResize?: () => void;

  connect(): void {
    this.onResize = debounce(() => this.draw());
    window.addEventListener("resize", this.onResize);
    this.draw();
    void this.load();
  }

  disconnect(): void {
    if (this.onResize) window.removeEventListener("resize", this.onResize);
  }

  private async load(): Promise<void> {
    const result = await fetchNumberOfRentalPropertiesConstructed(this.areaCodeValue);
    if (!result) return;
    this.graphData = result.graphData;
    this.descriptionIncreaseTarget.hidden = !result.isIncreaseTotal;
    this.descriptionDecreaseTarget.hidden = result.isIncreaseTotal;
    this.draw();
  }

  private draw(): void {
    renderChart(this.graphTarget, this.spec());
  }

  private spec(): ChartSpec {
    const sp = isSp();
    const tickFontSize = sp ? "10px" : "12px";
    const strokeWidth = sp ? 2 : 3;
    return {
      kind: "line",
      data: this.graphData as unknown as ChartSpec["data"],
      xKey: "year",
      xPadding: { left: 20, right: 20 },
      xTickFontSize: tickFontSize,
      yTickFontSize: tickFontSize,
      yUnit: "戸",
      yLocaleString: true,
      curve: "monotone",
      legendWrapperStyle: {
        "font-size": sp ? "10px" : "15px",
        "margin-left": "20px",
        "line-height": "40px",
      },
      series: [
        { key: "totalConstructed", name: "全体", stroke: "#FF367F", fill: "#FF367F", strokeWidth, unit: "戸" },
        { key: "totalWooden", name: "木造", stroke: "#66CDAA", fill: "#66CDAA", strokeWidth, unit: "戸" },
        { key: "totalNotWooden", name: "非木造", stroke: "#2C7CFF", fill: "#2C7CFF", strokeWidth, unit: "戸" },
      ],
      containerStyle: "width:100%;height:100%;min-width:0",
    };
  }
}

application.register(
  "site--prefecture-mansion--construction-count",
  SitePrefectureMansionConstructionCountController,
);
